import { Button, ButtonVariants, Dialog, FormControlSizes, HStack, Tooltip, useDisclosure } from '@talos/kyoko';
import { useCallback, useMemo } from 'react';

interface MarketSelectorPanelSaveControlsProps {
  onSave: () => void | Promise<void>;
  /** Requests closing the panel. Callers can use this to show the unsaved-changes confirmation instead of closing immediately. */
  onClose: () => void;
  /** Confirms the user wants to discard edits, so callers should reset draft state before closing the panel. */
  onCloseWithoutSaving?: () => void;
  /** Dismisses only the unsaved-changes confirmation dialog and keeps the panel open for editing. */
  onCancelClosingWithoutSavingConfirmation: () => void;
  showClosingWithoutSavingConfirmation: boolean;
  hasUnsavedChanges: boolean;
  saveDisabled?: boolean;
  saveLoading?: boolean;
}

function MarketSelectorPanelSaveControls({
  onSave,
  onClose,
  onCloseWithoutSaving,
  onCancelClosingWithoutSavingConfirmation,
  showClosingWithoutSavingConfirmation,
  hasUnsavedChanges,
  saveDisabled,
  saveLoading,
}: MarketSelectorPanelSaveControlsProps) {
  const saveDisabledBecauseUnchanged = !hasUnsavedChanges;
  const resolvedSaveDisabled = !!saveDisabled || saveDisabledBecauseUnchanged;
  const saveButtonDisabled = resolvedSaveDisabled || saveLoading;

  return (
    <>
      <HStack gap="spacing4" justifyContent="flex-end" p="spacing4">
        <Button onClick={onClose} size={FormControlSizes.Small} width="60px" disabled={saveLoading}>
          Close
        </Button>
        <Tooltip
          tooltip="No changes made"
          tooltipTestID="market-selector-save-disabled-tooltip"
          disabled={!saveDisabledBecauseUnchanged}
        >
          <Button
            width="60px"
            disabled={saveButtonDisabled}
            loading={saveLoading}
            variant={ButtonVariants.Primary}
            size={FormControlSizes.Small}
            onClick={onSave}
          >
            Save
          </Button>
        </Tooltip>
      </HStack>
      <Dialog
        isOpen={showClosingWithoutSavingConfirmation}
        close={onCancelClosingWithoutSavingConfirmation}
        onConfirm={onCloseWithoutSaving ?? onClose}
        title="Unsaved changes"
        cancelLabel="Cancel"
        confirmLabel="Confirm"
        confirmDisabled={saveLoading}
        preventClosing={saveLoading}
        variant={ButtonVariants.Primary}
        dataTestId="market-selector-close-without-saving-dialog"
        width="280px"
        usePortal={false}
        showClose={false}
      >
        There are unsaved changes. Confirm to close without saving.
      </Dialog>
    </>
  );
}

export interface UseMarketSelectorPanelSaveControlsParams {
  /** Persists the caller's current market selector draft. May be async while an API request is in flight. */
  onSave: () => void | Promise<void>;
  /** Closes the market selector panel when closing is allowed. */
  onClose: () => void;
  /** Optional discard path. Use this to reset local draft state before closing without saving. */
  onCloseWithoutSaving?: () => void;
  /** Whether the panel draft differs from the persisted/current selection. Controls save and close confirmation UX. */
  hasUnsavedChanges: boolean;
  /** Additional caller-owned save disable state, beyond the built-in "no changes" disable rule. */
  saveDisabled?: boolean;
  /** Whether a save request is currently pending. Disables close/save controls and shows the save spinner. */
  saveLoading?: boolean;
  /** Set false when the caller wants close requests to discard immediately instead of showing confirmation. */
  confirmClosingWithoutSaving?: boolean;
}

/**
 * Builds the bottom-panel save controls and a close handler that protects unsaved market selector edits.
 *
 * Callers render `panelBottomSuffix` in the Market Selector panel and wire `requestClose` anywhere the panel
 * can close, so clicking outside or pressing a Close button follows the same unsaved-changes flow.
 */
export function useMarketSelectorPanelSaveControls({
  onSave,
  onClose,
  onCloseWithoutSaving,
  hasUnsavedChanges,
  saveDisabled,
  saveLoading,
  confirmClosingWithoutSaving = true,
}: UseMarketSelectorPanelSaveControlsParams) {
  // Local confirmation state is owned by the hook so every close entry point shares one dialog.
  const {
    isOpen: showClosingWithoutSavingConfirmation,
    open: openClosingWithoutSavingConfirmation,
    close: closeClosingWithoutSavingConfirmation,
  } = useDisclosure();

  // Successful saves should also dismiss any stale close-confirmation dialog.
  const handleSave = useCallback(async () => {
    await onSave();
    closeClosingWithoutSavingConfirmation();
  }, [closeClosingWithoutSavingConfirmation, onSave]);

  // Confirmed discard closes the dialog first, then lets the caller reset drafts before the panel closes.
  const handleCloseWithoutSaving = useCallback(() => {
    closeClosingWithoutSavingConfirmation();
    (onCloseWithoutSaving ?? onClose)();
  }, [closeClosingWithoutSavingConfirmation, onClose, onCloseWithoutSaving]);

  // Central close gate used by click-outside, the footer Close button, and any caller-provided close affordance.
  const requestClose = useCallback(() => {
    if (confirmClosingWithoutSaving && hasUnsavedChanges) {
      if (showClosingWithoutSavingConfirmation) {
        handleCloseWithoutSaving();
        return;
      }

      openClosingWithoutSavingConfirmation();
      return;
    }

    closeClosingWithoutSavingConfirmation();
    onClose();
  }, [
    closeClosingWithoutSavingConfirmation,
    confirmClosingWithoutSaving,
    handleCloseWithoutSaving,
    hasUnsavedChanges,
    onClose,
    openClosingWithoutSavingConfirmation,
    showClosingWithoutSavingConfirmation,
  ]);

  // Keep the rendered footer stable so panel consumers can pass it through as a suffix without extra churn.
  const panelBottomSuffix = useMemo(
    () => (
      <MarketSelectorPanelSaveControls
        onSave={handleSave}
        onClose={requestClose}
        onCloseWithoutSaving={handleCloseWithoutSaving}
        onCancelClosingWithoutSavingConfirmation={closeClosingWithoutSavingConfirmation}
        showClosingWithoutSavingConfirmation={showClosingWithoutSavingConfirmation}
        hasUnsavedChanges={hasUnsavedChanges}
        saveDisabled={saveDisabled}
        saveLoading={saveLoading}
      />
    ),
    [
      closeClosingWithoutSavingConfirmation,
      handleCloseWithoutSaving,
      handleSave,
      hasUnsavedChanges,
      requestClose,
      saveDisabled,
      saveLoading,
      showClosingWithoutSavingConfirmation,
    ]
  );

  // Expose both the footer node and the guarded close handler; callers need to wire both for consistent UX.
  return useMemo(
    () => ({
      panelBottomSuffix,
      requestClose,
    }),
    [panelBottomSuffix, requestClose]
  );
}
